import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import { GraphqlErrorMessage } from "../../../components/ErrorMessage";
import { Loading } from "../../../components/LoadingFork";
import { withInstallation } from "../../../components/InstallationContext";
import { T, FONT_SANS } from "../constants";
import Panel from "../components/Panel";
import Toast from "../components/Toast";
import { useUi } from "../ui";
import VipPageShell from "../vip/components/VipPageShell";
import { contactQuery, featureByNameQuery, updateContactMutation } from "./queries";

const DEFAULT_SMS_TEMPLATE = `VIP ARRIVAL - {{guest_name}} ({{plate}}) has arrived at {{time}}.
{{note}}
Open: {{short_link}} - DRIVO Concierge`;
const DEFAULT_SANCTIONED_PLATE_SMS_TEMPLATE = "VIP ARRIVAL - sanctioned plate ({{plate}}) has arrived.";

const DEFAULT_EMAIL_SUBJECT_TEMPLATE = "VIP arrival · {{guest_name}} · {{plate}}";
const DEFAULT_SANCTIONED_PLATE_EMAIL_SUBJECT_TEMPLATE = "VIP arrival · sanctioned plate · {{plate}}";

const useStyles = makeStyles({
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: 16,
    color: T.primary,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
  blurb: { maxWidth: 760, marginBottom: 24, fontSize: 14, color: T.mutedFg },
  contactMeta: { display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" },
  chip: {
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.25)",
    color: "rgba(255,255,255,0.82)",
    padding: "6px 10px",
    fontSize: 12,
  },
  grid: {
    display: "grid",
    gap: 24,
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, 0.9fr)",
    alignItems: "start",
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
  },
  stack: { display: "grid", gap: 24 },
  panelBody: { padding: 20 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
  to: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    padding: "2px 8px",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: T.mutedFg,
    whiteSpace: "nowrap",
  },
  stage: { marginTop: 16, borderRadius: 12, background: T.surface, padding: 16 },
  bubble: {
    maxWidth: 340,
    borderRadius: 16,
    borderBottomLeftRadius: 2,
    background: T.primary,
    color: T.primaryFg,
    padding: "12px 16px",
    fontSize: 14,
    lineHeight: 1.6,
    whiteSpace: "pre-line",
  },
  meta: { marginTop: 6, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: T.mutedFg },
  subject: { margin: "4px 0 0", fontSize: 12, color: T.mutedFg },
  subjectValue: { color: T.fg },
  emailFrame: { marginTop: 16, overflow: "hidden", borderRadius: 12, border: `1px solid ${T.border}` },
  emailBanner: { background: T.primary, color: T.primaryFg, padding: "16px 20px" },
  emailBannerEyebrow: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.4em",
    color: "rgba(255,255,255,0.6)",
  },
  emailBannerTitle: { marginTop: 4, fontSize: 18, fontWeight: 300 },
  emailBody: { background: T.elevated, padding: 20, display: "grid", gap: 12 },
  chips: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  plate: {
    display: "inline-block",
    borderRadius: 8,
    border: `1.5px solid ${T.primary}`,
    background: "#fff",
    color: T.fg,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 6,
    border: "1px solid rgba(255,195,0,0.45)",
    background: "rgba(255,195,0,0.16)",
    color: T.fg,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  guest: { fontSize: 20, fontWeight: 500, color: T.fg },
  emailRows: { display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 8, columnGap: 16, margin: 0 },
  sanctionedEmailRows: { marginTop: 16 },
  emailRowFull: { gridColumn: "1 / -1" },
  emailKey: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: T.mutedFg },
  emailValue: { margin: 0, fontSize: 14, color: T.fg },
  cta: {
    display: "inline-block",
    borderRadius: 6,
    background: T.primary,
    color: T.primaryFg,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 600,
  },
  footNote: { margin: 0, fontSize: 11, color: T.mutedFg },
  editor: {
    width: "100%",
    minHeight: 180,
    boxSizing: "border-box",
    resize: "vertical",
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    padding: 12,
    fontSize: 14,
    lineHeight: 1.5,
    color: T.fg,
    outline: "none",
    fontFamily: `SFMono-Regular, Menlo, Consolas, monospace, ${FONT_SANS}`,
    "&:focus": { borderColor: T.primary, boxShadow: `0 0 0 2px ${T.primaryTint}` },
  },
  editorHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    margin: "18px 0 6px",
  },
  count: { color: T.mutedFg, fontSize: 12 },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 },
  secondaryButton: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    color: T.mutedFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { background: T.surface },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  table: { marginTop: 12, width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    borderBottom: `1px solid ${T.border}`,
    textAlign: "left",
    padding: "8px 0",
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: T.mutedFg,
  },
  tokenCell: { borderTop: `1px solid ${T.borderSoft}`, padding: "8px 16px 8px 0", verticalAlign: "top" },
  tokenButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    fontFamily: `SFMono-Regular, Menlo, Consolas, monospace, ${FONT_SANS}`,
    fontSize: 12,
    color: T.primary,
    cursor: "pointer",
  },
  meaning: { borderTop: `1px solid ${T.borderSoft}`, padding: "8px 0", color: T.mutedFg, verticalAlign: "top" },
  emptyVariables: { margin: "12px 0 0", color: T.mutedFg, fontSize: 14 },
  error: { marginBottom: 24 },
});

export function parseJsonValue(value) {
  if (!value) return null;
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  return null;
}

export function normalizeVariableName(variable) {
  return variable.replace(/^{{\s*/, "").replace(/\s*}}$/, "").trim();
}

export function toVariableToken(variable) {
  const normalized = normalizeVariableName(variable);
  return normalized ? `{{${normalized}}}` : "";
}

export function extractTemplateVariables(baseConfiguration) {
  const configuration = parseJsonValue(baseConfiguration);
  if (!configuration) return [];

  const groupedVariables = Array.isArray(configuration.groups)
    ? configuration.groups.reduce((result, group) => {
        if (Array.isArray(group.variables)) result.push(...group.variables);
        return result;
      }, [])
    : [];
  const variables = [
    ...(Array.isArray(configuration.variables) ? configuration.variables : []),
    ...groupedVariables,
  ];

  return variables
    .filter((variable) => variable && typeof variable.variable === "string" && normalizeVariableName(variable.variable))
    .map((variable) => ({
      ...variable,
      variable: toVariableToken(variable.variable),
      normalizedName: normalizeVariableName(variable.variable),
    }));
}

export function variablesForContactType(variables, contactType) {
  const entities = contactType === "SANCTIONED_PLATE"
    ? ["sanctionedPlate", "valetSession", "gate"]
    : ["booking", "rateCard", "contact", "gate"];
  return variables.filter((variable) => entities.includes(variable.entity));
}

export function renderTemplate(template, sampleValues) {
  return template.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, variable) => sampleValues[variable] || match);
}

export function sampleValuesFromVariables(variables) {
  return variables.reduce((result, variable) => {
    if (variable.sampleValue !== undefined && variable.sampleValue !== null) {
      result[variable.normalizedName] = String(variable.sampleValue);
    }
    return result;
  }, {});
}

export function defaultSmsTemplateForContact(contact) {
  return contact?.type === "SANCTIONED_PLATE"
    ? DEFAULT_SANCTIONED_PLATE_SMS_TEMPLATE
    : DEFAULT_SMS_TEMPLATE;
}

export function contactUpdateInput(contact, smsTemplate) {
  return {
    id: contact.id,
    name: contact.name,
    description: contact.description ?? null,
    email: contact.email ?? null,
    phone: contact.phone ?? null,
    smsTemplate,
  };
}

function ContactHeaderMeta({ contact }) {
  const s = useStyles();
  return (
    <div className={s.contactMeta}>
      <span className={s.chip}>
        {contact.type === "SANCTIONED_PLATE" ? "Sanctioned plate" : "Booking"}
      </span>
      <span className={s.chip}>{contact.phone || "No SMS number"}</span>
      {contact.email && <span className={s.chip}>{contact.email}</span>}
    </div>
  );
}

function EmailRow({ label, value, full = false }) {
  const s = useStyles();
  return (
    <div className={full ? s.emailRowFull : undefined}>
      <dt className={s.emailKey}>{label}</dt>
      <dd className={s.emailValue}>{value || "-"}</dd>
    </div>
  );
}

function ContactTemplatePage({ installation, match }) {
  const s = useStyles();
  const ui = useUi();
  const textareaRef = useRef(null);
  const contactId = match.params.contactId;
  const [draft, setDraft] = useState("");
  const [toast, setToast] = useState(null);
  const [mutationError, setMutationError] = useState(null);

  const { data = {}, loading, error, refetch } = useQuery(contactQuery, {
    variables: { id: contactId },
    fetchPolicy: "no-cache",
  });
  const {
    data: featureData = {},
    loading: featureLoading,
    error: featureError,
  } = useQuery(featureByNameQuery, {
    variables: { name: "TEMPLATE_VARS" },
    fetchPolicy: "no-cache",
  });
  const [updateContact, updateState] = useMutation(updateContactMutation);

  const contact = data.contact;
  const baseConfiguration = featureData.getFeatureByName?.baseConfiguration;
  const templateVariables = useMemo(() => {
    const variables = extractTemplateVariables(baseConfiguration);
    return variablesForContactType(variables, contact?.type);
  }, [baseConfiguration, contact?.type]);
  const original = contact ? contact.smsTemplate ?? defaultSmsTemplateForContact(contact) : "";
  const emailSample = useMemo(() => sampleValuesFromVariables(templateVariables), [templateVariables]);
  const preview = useMemo(() => renderTemplate(draft, emailSample), [draft, emailSample]);
  const emailSubjectTemplate = contact?.type === "SANCTIONED_PLATE"
    ? DEFAULT_SANCTIONED_PLATE_EMAIL_SUBJECT_TEMPLATE
    : DEFAULT_EMAIL_SUBJECT_TEMPLATE;
  const emailSubject = useMemo(() => renderTemplate(emailSubjectTemplate, emailSample), [emailSubjectTemplate, emailSample]);
  const isDirty = draft !== original;
  const isWrongInstallation = contact && installation?.id && contact.installationId !== installation.id;

  useEffect(() => {
    if (contact) setDraft(contact.smsTemplate ?? defaultSmsTemplateForContact(contact));
  }, [contact]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const insertToken = (token) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setDraft((value) => `${value}${token}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setDraft((value) => `${value.slice(0, start)}${token}${value.slice(end)}`);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = start + token.length;
      textarea.selectionEnd = start + token.length;
    });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!contact || !isDirty || updateState.loading || isWrongInstallation) return;

    setMutationError(null);
    try {
      await updateContact({
        variables: {
          contact: contactUpdateInput(contact, draft),
        },
      });
      await refetch();
      setToast("Design saved.");
    } catch (err) {
      setMutationError(err);
    }
  };

  if (loading) return <Loading />;
  if (error) return <GraphqlErrorMessage error={error} />;
  if (!contact) return <GraphqlErrorMessage error={new Error("Contact not found.")} />;

  if (isWrongInstallation) {
    return (
      <VipPageShell eyebrow="Admin - Configuration" title="VIP alert design">
        <Link className={s.backLink} to={match.url.replace(`/${contactId}`, "")}>
          Back to VIP routing
        </Link>
        <GraphqlErrorMessage error={new Error("This contact does not belong to the current installation.")} />
      </VipPageShell>
    );
  }

  return (
    <VipPageShell
      eyebrow="Admin - Configuration"
      title={contact ? `${contact.name} design` : "VIP alert design"}
      right={contact && <ContactHeaderMeta contact={contact} />}
    >
      <Link className={s.backLink} to={match.url.replace(`/${contactId}`, "")}>
        Back to VIP routing
      </Link>

      <p className={s.blurb}>
        These templates are used when VIP routing sends an alert to this contact.
      </p>

      {mutationError && <GraphqlErrorMessage className={s.error} error={mutationError} />}
      {featureError && <GraphqlErrorMessage className={s.error} error={featureError} />}

      <div className={s.grid}>
        <div className={s.stack}>
          <Panel>
            <form className={s.panelBody} onSubmit={save}>
              <div className={s.head}>
                <h2 className={s.title}>SMS</h2>
                <span className={s.to}>To {contact?.phone || "-"}</span>
              </div>

              <div className={s.stage}>
                <div className={s.bubble}>{preview || " "}</div>
                <div className={s.meta}>DRIVO - now</div>
              </div>

              <div className={s.editorHead}>
                <h3 className={s.title}>Template</h3>
                <span className={s.count}>{draft.length} characters</span>
              </div>
              <textarea
                aria-label="SMS template"
                ref={textareaRef}
                className={s.editor}
                disabled={updateState.loading}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />

              <div className={s.actions}>
                <button
                  type="button"
                  className={s.secondaryButton}
                  disabled={!isDirty || updateState.loading}
                  onClick={() => setDraft(original)}
                >
                  Cancel
                </button>
                <button type="submit" className={ui.primaryBtn} disabled={!isDirty || updateState.loading}>
                  Save
                </button>
              </div>
            </form>
          </Panel>

          <Panel>
            <div className={s.panelBody}>
              <div className={s.head}>
                <h2 className={s.title}>Email</h2>
                <span className={s.to}>To {contact?.email || "-"}</span>
              </div>
              <p className={s.subject}>
                Subject: <span className={s.subjectValue}>{emailSubject}</span>
              </p>

              <div className={s.emailFrame}>
                <div className={s.emailBanner}>
                  <div className={s.emailBannerEyebrow}>{installation?.name || "DRIVO"} · DRIVO</div>
                  <div className={s.emailBannerTitle}>
                    {contact.type === "SANCTIONED_PLATE" ? "VIP sanctioned plate arrival" : "VIP arrival"}
                  </div>
                </div>
                <div className={s.emailBody}>
                  {contact.type === "SANCTIONED_PLATE" ? (
                    <>
                      <div className={s.chips}>
                        <span className={s.plate}>{emailSample.plate || "{{plate}}"}</span>
                        <span className={s.tag}>VIP</span>
                      </div>
                      <dl className={`${s.emailRows} ${s.sanctionedEmailRows}`}>
                        <EmailRow full label="Plate" value={emailSample.plate || "{{plate}}"} />
                        <EmailRow
                          full
                          label="Description"
                          value={emailSample.sanctioned_plate_description || "{{sanctioned_plate_description}}"}
                        />
                        <EmailRow full label="Lot" value={emailSample.sanctioned_lot_name || "{{sanctioned_lot_name}}"} />
                        <EmailRow
                          full
                          label="Lot description"
                          value={emailSample.sanctioned_lot_description || "{{sanctioned_lot_description}}"}
                        />
                        <EmailRow
                          full
                          label="Gate description"
                          value={emailSample.gate_description || "{{gate_description}}"}
                        />
                      </dl>
                    </>
                  ) : (
                    <>
                      <div className={s.chips}>
                        <span className={s.plate}>{emailSample.plate || "{{plate}}"}</span>
                        <span className={s.tag}>VIP</span>
                      </div>
                      <div className={s.guest}>{emailSample.guest_name || "{{guest_name}}"}</div>
                      <dl className={s.emailRows}>
                        <EmailRow label="Name" value={emailSample.guest_name || "{{guest_name}}"} />
                        <EmailRow label="Arrived" value={emailSample.time || "{{time}}"} />
                        <EmailRow label="Email" value={emailSample.guest_email || "{{guest_email}}"} />
                        <EmailRow label="Phone" value={emailSample.guest_phone || "{{guest_phone}}"} />
                        <EmailRow
                          label="Booking"
                          value={`${emailSample.booking_start_day || "{{booking_start_day}}"} - ${
                            emailSample.booking_end_day || "{{booking_end_day}}"
                          }`}
                        />
                        <EmailRow full label="Notes" value={emailSample.note || "{{note}}"} />
                        <EmailRow
                          full
                          label="Gate description"
                          value={emailSample.gate_description || "{{gate_description}}"}
                        />
                      </dl>
                      <div>
                        <span className={s.cta}>Open in concierge console</span>
                      </div>
                      <p className={s.footNote}>
                        Sent to {contact?.name || "the assigned contact"} because they are the Contact on this booking.
                        Manage contacts in DRIVO - VIP routing.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Panel>
        </div>

        <Panel>
          <div className={s.panelBody}>
            <h2 className={s.title}>Merge variables</h2>
            {featureLoading ? (
              <div className={s.emptyVariables}>Loading merge variables...</div>
            ) : templateVariables.length ? (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Token</th>
                    <th className={s.th}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {templateVariables.map((variable) => (
                    <tr key={variable.variable}>
                      <td className={s.tokenCell}>
                        <button type="button" className={s.tokenButton} onClick={() => insertToken(variable.variable)}>
                          {variable.variable}
                        </button>
                      </td>
                      <td className={s.meaning}>{variable.description || variable.path || variable.entity || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={s.emptyVariables}>No merge variables are configured for this installation.</div>
            )}
          </div>
        </Panel>
      </div>

      {toast && <Toast>{toast}</Toast>}
    </VipPageShell>
  );
}

export default withInstallation(ContactTemplatePage);
